import {
  add,
  addDays,
  startOfWeek,
  isBefore,
  isEqual,
  format,
  isAfter,
  zonedTimeToUtc,
} from '@chilipiper/date-time'
import { ActiveRange } from '@chilipiper/api-client-user-availability'
import { CalendarEvent } from '@chilipiper/scheduling-shared'
import { AvailabilitiesWorkingHours } from './AvailabilitiesWorkingHoursContext'

const doScheduleDateRangesOverlap = (
  schedule1: { activeOn: ActiveRange },
  schedule2: { activeOn: ActiveRange }
): boolean => {
  // If either schedule is 'Always' active, they could overlap
  if (schedule1.activeOn.type === 'Always' || schedule2.activeOn.type === 'Always') {
    return true
  }

  // Helper to get date range
  const getRange = (schedule: { activeOn: ActiveRange }) => {
    const activeOn = schedule.activeOn
    if (activeOn.type === 'From') {
      return { start: new Date(activeOn.from), end: null }
    } else if (activeOn.type === 'To') {
      return { start: null, end: new Date(activeOn.to) }
    } else if (activeOn.type === 'FromTo') {
      return { start: new Date(activeOn.range.startsAt), end: new Date(activeOn.range.endsAt) }
    }
    return null
  }

  const range1 = getRange(schedule1)
  const range2 = getRange(schedule2)

  // If we can't determine ranges, assume they could overlap
  if (!range1 || !range2) {
    return false
  }

  if (range1.start && range2.end && isAfter(range1.start, range2.end)) {
    return false
  }
  if (range1.end && range2.start && isBefore(range1.end, range2.start)) {
    return false
  }

  return true
}

export const findOverlappingStrictReservedWorkingHours = (
  slot: CalendarEvent,
  isEventStrict: boolean,
  availabilitiesWorkingHours: AvailabilitiesWorkingHours,
  currentScheduleActiveOn?: ActiveRange
) => {
  const [day, , scheduleId] = slot.id?.split(':') ?? []

  return availabilitiesWorkingHours[day as keyof AvailabilitiesWorkingHours]
    .filter(item => {
      const { value: timeItem, isStrict, origin } = item

      if (!isEventStrict && !isStrict) {
        return false
      }

      const isSameOrigin = scheduleId === origin.id
      if (isSameOrigin) {
        return false
      }

      // Check if the schedule date ranges overlap first
      const otherSchedule = origin.schedule
      if (currentScheduleActiveOn && otherSchedule.type === 'CustomSchedule') {
        if (
          !doScheduleDateRangesOverlap(
            { activeOn: currentScheduleActiveOn },
            { activeOn: otherSchedule.activeOn }
          )
        ) {
          return false
        }
      }

      const start = getCurrentWeekdayDate(slot.start.getDay(), timeItem.startsAt)
      const end = new Date(computeEndDate(start.toISOString(), timeItem.duration) ?? '')

      return (
        !isBefore(end, slot.start) &&
        !isAfter(start, slot.end) &&
        !isEqual(start, slot.end) &&
        !isEqual(end, slot.start)
      )
    })
    .map(item => {
      const { isStrict } = item

      return {
        result: item,
        day: day,
        isConflictCausedByEvent: isEventStrict && !isStrict,
      }
    })
}

export const computeEndDate = (date?: string, duration?: string) => {
  if (!date || !duration) {
    return
  }

  return add(new Date(date), {
    seconds: parseInt(duration.replace(' milliseconds', '')) / 1000,
  }).toISOString()
}

export const invertedWeekDayMap = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
} as const

export const createDefaultWorkInterval = () => ({
  duration: '28800000 milliseconds',
  startsAt: '09:00:00',
})

export const defaultWorkingHours = {
  monday: [createDefaultWorkInterval()],
  tuesday: [createDefaultWorkInterval()],
  wednesday: [createDefaultWorkInterval()],
  thursday: [createDefaultWorkInterval()],
  friday: [createDefaultWorkInterval()],
  saturday: [],
  sunday: [],
}

export const getCurrentWeekdayDate = (weekday: number, hour: string) => {
  const startOfCurrentWeek = startOfWeek(new Date())
  const currentWeekdayDate = addDays(startOfCurrentWeek, weekday)

  const [hours, minutes, seconds] = hour.split(':')
  currentWeekdayDate.setHours(parseInt(hours))
  currentWeekdayDate.setMinutes(parseInt(minutes))
  currentWeekdayDate.setSeconds(parseInt(seconds))
  return currentWeekdayDate
}

export const getCurrentWeekdayUTCDate = (
  weekday: number,
  startsAt: string,
  timezone: string,
  calendarRangeStart: Date
) => {
  const startOfCurrentWeek = startOfWeek(calendarRangeStart)
  const currentWeekdayDate = addDays(startOfCurrentWeek, weekday)

  const fullDateTime = `${format(currentWeekdayDate, 'yyyy-MM-dd')}T${startsAt}`

  return zonedTimeToUtc(fullDateTime, timezone)
}
